import Card from "@ui-kit/data-display/Card/Card.web";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import Button from "@ui-kit/form/Button/Button.web";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Link from "next/link";
import { useTranslation } from "@shared/utils/internationalization";
import type { OrderSummarySharedProps } from "./OrderSummary.props";

export default function OrderSummary(props: OrderSummaryProps): JSX.Element {
  const { variant, ...rest } = props;
  return variant === "compact" ? (
    <OrderSummary.Compact {...rest} />
  ) : (
    <OrderSummary.Full {...rest} />
  );
}

OrderSummary.Full = function OrderSummaryFull(
  props: Omit<OrderSummaryProps, "variant">
) {
  const {
    transactionId,
    isLoggedIn,
    isConfirmationEmailEnabled,
    isLoading,
    basket,
  } = props;

  const { t } = useTranslation("checkout");

  if (isLoading)
    return (
      <Card borderRadius="md">
        <Flex gap="md" direction="column">
          <Text fontSize="lg" fontWeight={700}>
            {t("Order Summary")}
          </Text>
          <Skeleton h="element.lg" />
          <Skeleton h="element.lg" />
          <Skeleton h="element.lg" />
        </Flex>
      </Card>
    );

  const isWaitlist = basket?.is_waitlist;

  return (
    <>
      <Card borderRadius="md" data-testid="order-summary-full">
        <Flex direction="column" gap="md">
          <Text fontSize="lg" fontWeight={700}>
            {t("Order Summary")}
          </Text>
          <Flex direction="column" gap="xs" fontSize="md">
            {transactionId && (
              <Flex
                justify="space-between"
                flexWrap={{ base: "wrap", lg: "nowrap" }}
              >
                <Text>{!isWaitlist ? t("Order ID") : t("Waitlist ID")}</Text>
                <Text
                  fontWeight={700}
                  align="right"
                  wordBreak="break-all"
                  width={{ base: "100%", lg: "auto" }}
                >
                  {transactionId}
                </Text>
              </Flex>
            )}
            {basket?.email && (
              <Flex
                justify="space-between"
                flexWrap={{ base: "wrap", lg: "nowrap" }}
              >
                <Text>
                  {!isWaitlist ? (
                    <>
                      {isConfirmationEmailEnabled ? t("Order sent to") : null}
                    </>
                  ) : (
                    t("Email sent to")
                  )}
                </Text>
                <Text
                  fontWeight={700}
                  align="right"
                  wordBreak="break-all"
                  width={{ base: "100%", lg: "auto" }}
                >
                  {basket?.email}
                </Text>
              </Flex>
            )}
          </Flex>
          <Flex fontSize="xl" justify="space-between">
            <Text fontWeight={700}>
              {!isWaitlist ? t("Total paid") : t("Total pending")}
            </Text>
            <Text fontWeight={600} align="right">
              <CurrencyFormat
                value={basket?.total_price}
                currency={basket?.currency}
                includeSuffix
                displayType="text"
                data-testid="order-summary-total"
              />
            </Text>
          </Flex>
          {isLoggedIn && transactionId && (
            <Flex direction="column" gap="md">
              <Link
                href={
                  !isWaitlist
                    ? `/account/my-orders/${transactionId}/`
                    : `/account/waitlists/` // Order confirmation pages are ephemeral, so assume _this_ order will always be at the top
                }
                target="_blank"
                passHref
              >
                <Button.Outline
                  as="span"
                  w="100%"
                  data-testid="order-summary-view-order"
                >
                  {!isWaitlist ? t("View order") : t("View waitlist")}
                </Button.Outline>
              </Link>
            </Flex>
          )}
        </Flex>
      </Card>
    </>
  );
};

OrderSummary.Compact = function OrderSummaryCompact(
  props: Omit<OrderSummaryProps, "variant">
) {
  const { totalPrice, currency, isLoading } = props;
  const { t } = useTranslation("checkout");

  return (
    <>
      <Flex
        direction="column"
        gap="md"
        data-testid="order-summary-compact"
        fontSize="sm"
        fontWeight="700"
        width="full"
      >
        <Flex align="center">
          <Text width="100%" textAlign="center">
            {t("Order Summary")}
          </Text>
          {/* Close button is a link to the homepage */}
          <Link href="/" passHref>
            <IconButton.Solid
              as="span"
              iconName="X"
              iconColor="font.dark"
              aria-label="Back to main page"
              size="sm"
              iconSize="24px"
              colorPalette="ghost"
              width="element.3xl"
              height="element.3xl"
              ml="auto"
              data-testid="go-to-main-page"
            />
          </Link>
        </Flex>
        {isLoading ? (
          <Skeleton h="element.lg" w="100%" />
        ) : (
          <Flex justify="space-between">
            <Text>{t("Total paid")}</Text>
            <Text>
              <CurrencyFormat
                value={totalPrice}
                currency={currency}
                includeSuffix
                displayType="text"
                data-testid="order-summary-total"
              />
            </Text>
          </Flex>
        )}
      </Flex>
    </>
  );
};

export type OrderSummaryProps = OrderSummarySharedProps & {
  // Add any web specific props here.
};
